/**
 * KIANUSH Cart Management System
 * Simple, reliable cart operations for Shopify
 */

class KianushCart extends HTMLElement {
  constructor() {
    super();
    this.cart = null;
    this.isUpdating = false;
  }

  connectedCallback() {
    this.fetchCart();
  }

  // Get current cart state
  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      this.cart = await response.json();
      this.dispatchCartUpdate();
      return this.cart;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  // Add item to cart
  async addToCart(variantId, quantity = 1, properties = {}) {
    if (this.isUpdating) return false;
    this.isUpdating = true;

    try {
      const formData = {
        items: [{
          id: parseInt(variantId),
          quantity: quantity,
          properties: properties
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(formData)
      });

      // Even if response is not ok, items might have been partially added
      // Always refresh cart to get actual state
      await this.fetchCart();
      
      // Return true even for partial success (422 errors often mean some items were added)
      return true;

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError('Fehler beim Hinzufügen zum Warenkorb');
      return false;
    } finally {
      this.isUpdating = false;
    }
  }

  // Update item quantity
  async updateQuantity(itemKey, quantity) {
    if (this.isUpdating) return false;
    this.isUpdating = true;

    try {
      const updates = {};
      updates[itemKey] = parseInt(quantity);

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) throw new Error('Failed to update quantity');

      // Refresh cart state
      await this.fetchCart();
      return true;

    } catch (error) {
      console.error('Error updating quantity:', error);
      this.showError('Fehler beim Aktualisieren der Menge');
      return false;
    } finally {
      this.isUpdating = false;
    }
  }

  // Remove item completely
  async removeItem(itemKey) {
    return this.updateQuantity(itemKey, 0);
  }

  // Update item properties using cart/change.js (no remove/re-add)
  async updateProperties(itemKey, properties) {
    if (this.isUpdating) return false;
    this.isUpdating = true;

    try {
      // Find the item and its index
      const itemIndex = this.cart.items.findIndex(item => item.key === itemKey);
      if (itemIndex === -1) {
        console.error('Item not found:', itemKey);
        return false;
      }
      
      const item = this.cart.items[itemIndex];
      // Use line number (1-based) instead of key for more reliable updates
      const lineNumber = itemIndex + 1;

      // Use cart/change.js to update properties in-place
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          line: lineNumber,  // Use line number instead of id/key
          quantity: item.quantity,
          properties: properties
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update properties');
      }

      // Get updated cart from response
      const updatedCart = await response.json();
      
      // Find the old item and new item to track key change
      const oldItem = this.cart.items[itemIndex];
      const newItem = updatedCart.items[itemIndex];
      
      // Update our cart state
      this.cart = updatedCart;
      
      // Dispatch cart update with both old and new keys
      const event = new CustomEvent('cart:updated', {
        detail: {
          cart: this.cart,
          itemCount: this.getItemCount(),
          total: this.getTotal(),
          isEmpty: this.isEmpty(),
          isPropertyUpdate: true,
          oldItemKey: itemKey,
          newItemKey: newItem ? newItem.key : itemKey,
          itemIndex: itemIndex
        }
      });
      document.dispatchEvent(event);
      return true;

    } catch (error) {
      console.error('Error updating properties:', error);
      // Fallback to old method if change.js fails
      const success = await this.removeItem(itemKey);
      if (success) {
        return this.addToCart(item.variant_id, item.quantity, properties);
      }
      return false;
    } finally {
      this.isUpdating = false;
    }
  }

  // Change item variant (for size changes etc.)
  async changeVariant(itemKey, newVariantId) {
    if (this.isUpdating) return false;
    this.isUpdating = true;

    try {
      // Find the item to get current quantity and properties
      const itemIndex = this.cart.items.findIndex(item => item.key === itemKey);
      if (itemIndex === -1) {
        console.error('Item not found:', itemKey);
        return false;
      }
      
      const item = this.cart.items[itemIndex];
      
      // Use cart/change.js to change variant
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          line: itemIndex + 1,  // Use line number (1-based)
          quantity: item.quantity,
          id: newVariantId  // New variant ID
        })
      });

      if (!response.ok) {
        throw new Error('Failed to change variant');
      }

      // Refresh cart to get updated state
      await this.fetchCart();
      return true;
      
    } catch (error) {
      console.error('Error changing variant:', error);
      // Fallback: remove and re-add
      const item = this.cart.items.find(item => item.key === itemKey);
      if (item) {
        const success = await this.removeItem(itemKey);
        if (success) {
          return this.addToCart(newVariantId, item.quantity, item.properties || {});
        }
      }
      return false;
    } finally {
      this.isUpdating = false;
    }
  }

  // Get cart count
  getItemCount() {
    return this.cart ? this.cart.item_count : 0;
  }

  // Get cart total
  getTotal() {
    return this.cart ? this.cart.total_price : 0;
  }

  // Check if cart is empty
  isEmpty() {
    return !this.cart || this.cart.items.length === 0;
  }

  // Dispatch cart update event
  dispatchCartUpdate() {
    const event = new CustomEvent('cart:updated', {
      detail: {
        cart: this.cart,
        itemCount: this.getItemCount(),
        total: this.getTotal(),
        isEmpty: this.isEmpty()
      }
    });
    document.dispatchEvent(event);
  }

  // Show error message
  showError(message) {
    // Create a simple toast notification
    const toast = document.createElement('div');
    toast.className = 'cart-error-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc2626;
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      z-index: 10000;
      font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    `;
    
    document.body.appendChild(toast);
    
    // Remove after 4 seconds
    setTimeout(() => {
      toast.remove();
    }, 4000);
  }
}

// Define custom element only if not already defined
if (!customElements.get('kianush-cart')) {
  customElements.define('kianush-cart', KianushCart);
}

// Global cart instance - check if already exists
if (!window.KianushCart) {
  window.KianushCart = document.querySelector('kianush-cart') || document.createElement('kianush-cart');
  if (!document.querySelector('kianush-cart')) {
    document.body.appendChild(window.KianushCart);
  }
}